
import { computed, ref, watch, inject, useRoute, nextTick, onMounted, useContext } from '@nuxtjs/composition-api';
import { storeToRefs } from 'pinia';
import useTlmCart from '~/composable/useTlmCart';
import { useChosenVariantDetailsStore } from '~/stores/useChosenVariantDetailsStore';
import usePlaymix from '~/composable/usePlaymix';
import useTracking from '~/composable/tracking/useTracking';
import useUtilities from '~/composable/useUtilities';
import usePlayInfoModal from '@/composable/usePlayInfoModal';
import { OFFERED_TO_CUSTOMER_TYPE } from '@/constants/OfferConstants';
import KeepSubscriptionDiscount from '~/components/Discounts/KeepSubscriptionDiscount.vue';

export default {
  components: { KeepSubscriptionDiscount },
  props: {
    defaultChannel: {
      type: String,
      default: null
    },
    skuInStock: {
      type: Boolean,
      default: null
    },
    offeredToCustomerType: {
      type: Array,
      default: () => []
    },
    hasSpecialPrices: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    const { app } = useContext()
    const { formatPrice } = useUtilities()
    const { track } = useTracking()
    const keepSubscriptionModal = ref(false)
    const flipper = inject('flipper')
    const reveal = ref(false)
    const selected = ref(null)
    const { getSubscriptionName } = useUtilities()
    const chosenVariantDetailsStore = useChosenVariantDetailsStore()
    const { isPayGSubscription } = useTlmCart(app)
    const { selectedPlayServices } = usePlaymix('playmix-4', app.$hubble)
    const {
      chosenInstallment,
      productDetails
    } = storeToRefs(chosenVariantDetailsStore)
    const playmixActive = ref(false);
    const keepSubscriptionActive = ref(false);
    const priceMatrix = computed(() => productDetails?.value?.chosenVariantPriceMatrix)
    const chosenVariantSku = computed(() => productDetails?.value?.chosenVariantSku)
    const masterProductSlug = computed(() => productDetails?.value?.masterProductSlug)
    const canCloseFlipper = ref(true)
    const { showModal: playInfoModalVisible } = usePlayInfoModal()
    const route = useRoute()
    const categorySlug = route.value.params.categoryslug.toLowerCase()
    const currentSubscriptionChosen = ref(false)
    const newOption = {
      channelReferenceID: null,
      installmentOptions: null,
      channelKey: '',
      productBundlePrice: null,
      name: 'KeepCurrentSubscription'
    };
    const priceMatrixWithCash = computed(() => {
      let priceMatrixWithAllOptions = []
      priceMatrixWithAllOptions = priceMatrix.value?.subscriptionBundleOptions

      if (!priceMatrixWithAllOptions?.some(option => option.name === newOption.name)) {
        priceMatrixWithAllOptions?.unshift(newOption);
      }
      if (priceMatrixWithAllOptions && priceMatrix.value?.cashOption !== null) {
        priceMatrixWithAllOptions.push(priceMatrix.value?.cashOption)
      }
      return priceMatrixWithAllOptions
    })

    // eslint-disable-next-line complexity
    const selectedChannel = computed(() => {
      const defaultVal = priceMatrix?.value?.subscriptionBundleOptions.find(bundle => bundle.channelReferenceID === props.defaultChannel)
      if (selected.value === null || selected.value === undefined) {
        if (defaultVal !== undefined) {
          return defaultVal
        }
        return priceMatrix?.value?.subscriptionBundleOptions[0]
      }
      return priceMatrixWithCash.value && selected.value >= 0 ? priceMatrixWithCash.value[selected.value] : defaultVal
    })

    watch(selectedChannel, () => {
      emitChosenChannel()
    })

    watch(priceMatrix, () => {
      if (selectedChannel?.value?.name === 'KeepCurrentSubscription') {
        selected.value = null
      }
    })

    const emitChosenChannel = () => {
      selected.value = priceMatrixWithCash.value?.indexOf(selectedChannel.value)
      emit('channelChosen', selectedChannel.value)
    }

    const trackChange = (close = false) => {
      track('changeSubscription', { chosenInstallment, channel: selectedChannel, subscriptionName: selectedChannelName.value, productDetails, categorySlug, close })
    }
    const closeKeepSubscriptionModal = () => {
      keepSubscriptionModal.value = false
      track('keepSubscriptionModal', { close: true })
      setTimeout(() => { // wait for internal v-dialog close event so flipper doesn't also close
        canCloseFlipper.value = true
      }, 200)
    }

    const openKeepSubscriptionModal = () => {
      keepSubscriptionModal.value = true
      canCloseFlipper.value = false
      track('keepSubscriptionModal', { open: true })
    }
    const openFlipper = () => {
      flipper.open(reveal)
      // temporarily removing it so that it can be UX tested whether removing scroll helps customer find Keep Sub easily
      // setTimeout(() => { // wait for animation
      //   const scrollContainer = document.querySelector('.custom-scrollbar');
      //   const options = scrollContainer?.querySelectorAll('.v-badge');
      //   const element = Array.from(options)[selected.value]
      //   scrollContainer.scrollTo({ top: element.offsetTop, behavior: 'smooth' });
      // }, 100)
    }
    const closeFlipper = () => {
      if (canCloseFlipper.value && !playmixActive.value && !keepSubscriptionActive.value) {
        return flipper.close(reveal)
      }
    }

    // Prevent flipper from closing when Play info modal opens/closes
    watch(playInfoModalVisible, (isOpen) => {
      if (isOpen) {
        canCloseFlipper.value = false
      } else {
        setTimeout(() => {
          canCloseFlipper.value = true
        }, 200)
      }
    })

    // eslint-disable-next-line complexity
    const close = () => {
      // If a PlayMix option is currently selected, and the user has exactly the
      // required number of services picked, make sure the committed selection
      // is up-to-date before closing (covers the case where user clicks outside
      // instead of triggering success from the selector)
      try {
        const mixBundleName = selectedChannel.value?.subscription?.isMixProduct ? selectedChannel.value?.subscription?.bundleName : null
        if (mixBundleName) {
          const { data: mixData, selection: mixSelection, selectedPlayServices: committed } = usePlaymix(mixBundleName, app.$hubble)
          const limit = mixData?.value?.mixVasLimit
          if (limit && Array.isArray(mixSelection?.value)) {
            const filled = mixSelection.value.filter(v => v !== null)
            if (filled.length === limit) {
              const selectedServices = mixData.value.productOfferingDTOWithContent.filter((_item, i) => mixSelection.value.includes(i))
              committed.value = selectedServices.map(val => val.productOfferingDTO.id)
            }
          }
        }
      } catch (e) {}

      closeFlipper()
      if (canCloseFlipper.value) {
        trackChange(true)
      }
    }

    const selectedChannelName = computed(() => {
      if (selectedChannel.value?.channelKey === 'Cash') {
        return 'Telefon uden abonnement'
      }

      return selectedChannel.value?.subscription?.displayName
    })

    const onPlaymixSuccess = () => {
      playmixActive.value = false;
      keepSubscriptionActive.value = false; // Ensure keep subscription is also reset when play mix is successful
      closeFlipper()
      // @todo save choosen playmix options or something
    }
    // eslint-disable-next-line complexity
    const handleKeepExistingSubscription = (data) => {
      const foundOption = priceMatrixWithCash.value.find(option => option.name === 'KeepCurrentSubscription');
      if (foundOption && data?.calculationResponse.value?.discount !== null) {
        foundOption.subscription = data?.calculationResponse.value?.subscriptionBundleDTO;
        foundOption.channelKey = data?.calculationResponse.value?.discountValidForSubscriptionID;
        foundOption.productBundlePrice = data?.calculationResponse.value?.existingSubscriptionWithPrice?.productBundlePrice;
        foundOption.installmentOptions = data?.calculationResponse.value?.existingSubscriptionWithPrice?.installmentOptions;
        foundOption.subscription = data?.calculationResponse.value.subscriptionBundle;
        if (data?.calculationResponse?.value.subscriptionBundle?.isMixProduct) {
          const mixServiceList = []
          let mixPos = data?.calculationResponse.value.subscriptionsDTO.subscriptionList.filter(service => service.productOfferingType === 'PLAN')
          if (mixPos.length === 0) {
            mixPos = data?.calculationResponse.value.subscriptionsDTO.subscriptionList.filter(service => service.productOfferingType === 'MIX_VAS_PRODUCT')
          }
          mixPos.forEach(po => mixServiceList.push(po.id))
          selectedPlayServices.value = mixServiceList
        }
      }
      if (data?.calculationResponse.value?.discount === null) {
        const index = priceMatrixWithCash.value.indexOf(foundOption);
        // If the foundOption exists in the array, remove it
        if (index !== -1) {
          priceMatrixWithCash.value.splice(index, 1);
        }
      }
    }

    const onPickPlaymixOption = (i, id) => {
      const { data, selected: sel } = usePlaymix(id)
      playmixActive.value = true
      keepSubscriptionActive.value = false // Reset keep subscription active when switching to play mix
      nextTick(() => {
        if (sel.value.length === data.value?.mixVasLimit && i === selected.value) {
          playmixActive.value = false;
        }
      })
    }

    onMounted(() => {
      // calculatePriceMatrixWithCash()
      emitChosenChannel()
    })

    const isChangeSubscriptionAllowed = computed(() => {
      return props.offeredToCustomerType === null ? true : props.offeredToCustomerType?.includes(OFFERED_TO_CUSTOMER_TYPE.EXISTING)
    })

    return {
      isPayGSubscription,
      currentSubscriptionChosen,
      masterProductSlug,
      chosenVariantSku,
      getSubscriptionName,
      isChangeSubscriptionAllowed,
      onPickPlaymixOption,
      close,
      trackChange,
      formatPrice,
      onPlaymixSuccess,
      closeKeepSubscriptionModal,
      openKeepSubscriptionModal,
      openFlipper,
      closeFlipper,
      keepSubscriptionActive,
      playmixActive,
      productDetails,
      canCloseFlipper,
      priceMatrix,
      priceMatrixWithCash,
      flipper,
      reveal,
      selectedChannel,
      selected,
      selectedChannelName,
      selectedChannelDiscount: computed(() => selectedChannel.value?.productBundlePrice?.discount),
      selectedChannelPrice: computed(() => selectedChannel.value?.productBundlePrice?.bundlePrice),
      isCash: computed(() => selectedChannel.value?.channelKey === 'Cash'),
      isKeepCurrentSubscription: computed(() => selectedChannel.value?.channelKey === 'KeepCurrentSubscription'),
      keepSubscriptionModal,
      handleKeepExistingSubscription
    }
  }
}
